import React, { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import {
  ContractLeaseActions,
  ContractLeaseData,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
} from '../../../components';
import { createSlug, stringify } from '../../../util/urlHelpers';
import { getStatusLabel } from '../../../util/listing';
import { formatMoney, subUnitsToMoney } from '../../../util/currency';
import { getContractDurationInMonths } from '../../../util/dates';
import { SEARCH_PAGE_COMPANY_VIEW_PARAM } from '../../../util/search';

import css from './TableViewPanel.module.css';

const TableViewPanel = ({ listings, intl, isGrouped, selectedStatusFilter }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const toggleRow = id => {
    setExpandedRow(prev => (prev === id ? null : id));
  };

  const toggleGroupRow = id => {
    setExpandedGroup(prev => (prev === id ? null : id));
  };

  const fromState =
    typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '';

  const columnConfig = {
    myContracts: [
      { id: 'model', label: 'TableView.model' },
      { id: 'status', label: 'TableView.status' },
      { id: 'contractId', label: 'TableView.contractId' },
      { id: 'contractEndDate', label: 'TableView.endDate' },
      { id: 'sellingDealer', label: 'TableView.dealer' },
    ],
    toBeInspected: [
      { id: 'model', label: 'TableView.model' },
      { id: 'status', label: 'TableView.status' },
      { id: 'contractId', label: 'TableView.contractId' },
      { id: 'contractEndDate', label: 'TableView.endDate' },
      { id: 'sellingDealer', label: 'TableView.dealer' },
      { id: 'receivedDate', label: 'TableView.receivedDate' },
    ],
    toBeRefurbished: [
      { id: 'model', label: 'TableView.model' },
      { id: 'status', label: 'TableView.status' },
      { id: 'serialNumber', label: 'TableView.serialNumber' },
      { id: 'ownerAfterContract', label: 'TableView.ownerAfterContract' },
    ],
    prepareForReselling: [
      { id: 'model', label: 'TableView.model' },
      { id: 'status', label: 'TableView.status' },
      { id: 'serialNumber', label: 'TableView.serialNumber' },
      { id: 'ownerAfterContract', label: 'TableView.ownerAfterContract' },
    ],
    forSale: [
      { id: 'model', label: 'TableView.model' },
      { id: 'status', label: 'TableView.status' },
      { id: 'serialNumber', label: 'TableView.serialNumber' },
      { id: 'ownerAfterContract', label: 'TableView.ownerAfterContract' },
      { id: 'price', label: 'TableView.price' },
    ],
    archived: [
      { id: 'model', label: 'TableView.model' },
      { id: 'status', label: 'TableView.status' },
      { id: 'contractId', label: 'TableView.contractId' },
      { id: 'contractEndDate', label: 'TableView.endDate' },
      { id: 'sellingDealer', label: 'TableView.dealer' },
    ],
    default: [
      { id: 'model', label: 'TableView.model' },
      { id: 'status', label: 'TableView.status' },
      { id: 'contractId', label: 'TableView.contractId' },
      { id: 'contractEndDate', label: 'TableView.endDate' },
      { id: 'residualValue', label: 'TableView.residualValue' },
      { id: 'sellingDealer', label: 'TableView.dealer' },
    ],
  };

  const activeColumns = columnConfig[selectedStatusFilter] || columnConfig.default;

  const renderCell = (listing, col) => {
    const publicData = listing.attributes?.publicData || {};
    const metadata = listing.attributes?.metadata || {};
    const price = listing.attributes?.price || {};

    switch (col.id) {
      case 'model':
        return publicData.dimrModel || '—';

      case 'status':
        return getStatusLabel(metadata.status);

      case 'contractId':
        return publicData.contractId || '—';

      case 'contractEndDate':
        return publicData.contractEndDate ? (
          <FormattedDate
            value={publicData.contractEndDate}
            year="numeric"
            month="short"
            day="2-digit"
          />
        ) : (
          '—'
        );

      case 'sellingDealer':
        return metadata?.contractEndOwnerCompanyId ? (
          <NamedLink
            name="SearchPagePublished"
            to={{
              search: stringify({
                [SEARCH_PAGE_COMPANY_VIEW_PARAM]: metadata?.contractEndOwnerCompanyId,
              }),
            }}
          >
            {metadata?.contractEndOwnerCompanyName}
          </NamedLink>
        ) : (
          metadata?.contractEndOwnerCompanyName || '-'
        );

      // received date = date when listing was marked as received = pendingReview state
      case 'receivedDate':
        return metadata.statusHistory ? (
          <FormattedDate
            value={metadata.statusHistory.find(item => item.status === 'pendingReview')?.at}
            year="numeric"
            month="short"
            day="2-digit"
          />
        ) : (
          '—'
        );

      case 'serialNumber':
        return publicData.serialNumber || '—';

      case 'ownerAfterContract':
        return metadata?.contractEndOwnerCompanyId ? (
          <NamedLink
            name="SearchPagePublished"
            to={{
              search: stringify({
                [SEARCH_PAGE_COMPANY_VIEW_PARAM]: metadata?.contractEndOwnerCompanyId,
              }),
            }}
          >
            {metadata?.contractEndOwnerCompanyName}
          </NamedLink>
        ) : (
          metadata?.contractEndOwnerCompanyName || '-'
        );

      case 'price':
        return listing.attributes?.price
          ? formatMoney(intl, subUnitsToMoney(price?.amount, price?.currency))
          : '-';

      case 'residualValue':
        return publicData.residualValue
          ? formatMoney(
              intl,
              subUnitsToMoney(publicData.residualValue, publicData.locale === 'no' ? 'NOK' : 'SEK')
            )
          : '-';

      default:
        return '-';
    }
  };

  const renderMenu = listing => (
    <Menu contentPlacement="left">
      <MenuLabel>
        <span className={css.menuTrigger}>⋮</span>
      </MenuLabel>
      <MenuContent className={css.menuContent}>
        <MenuItem key="view" className={css.menuItem}>
          <NamedLink
            name="ListingPage"
            params={{
              id: listing.id.uuid,
              slug: createSlug(listing.attributes.title || ''),
            }}
            to={{ state: { from: fromState } }}
            className={css.menuLink}
          >
            <FormattedMessage id="TableView.viewDetails" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="print" disabled className={css.menuItem}>
          <FormattedMessage id="TableView.printTable" />
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  return (
    <div className={css.tableWrapper}>
      <table className={css.table}>
        <thead>
          {isGrouped ? (
            <tr>
              <th>
                <FormattedMessage id="TableView.contractId" />
              </th>
              <th>
                <FormattedMessage id="TableView.items" />
              </th>
              <th>
                <FormattedMessage id="TableView.models" />
              </th>
              <th>
                <FormattedMessage id="TableView.endDate" />
              </th>
              <th>
                <FormattedMessage id="TableView.months" />
              </th>
              <th>
                <FormattedMessage id="TableView.residualValue" />
              </th>
            </tr>
          ) : (
            <tr>
              {activeColumns.map(col => (
                <th key={col.id}>
                  <FormattedMessage id={col.label} />
                </th>
              ))}
              <th></th>
            </tr>
          )}
        </thead>

        <tbody>
          {isGrouped
            ? listings.map(group => {
                const { contractId, groupItems } = group;
                const models = [
                  ...new Set(groupItems.map(i => i.attributes?.publicData?.dimrModel)),
                ].join(', ');
                const totalResidualValue = groupItems.reduce(
                  (sum, item) => sum + (item.attributes?.publicData?.residualValue || 0),
                  0
                );
                const currency =
                  groupItems[0]?.attributes?.publicData?.locale === 'no' ? 'NOK' : 'SEK';

                return (
                  <React.Fragment key={contractId}>
                    <tr onClick={() => toggleGroupRow(contractId)} className={css.clickableRow}>
                      <td>{contractId}</td>
                      <td>{groupItems.length}</td>
                      <td>{models}</td>
                      <td>
                        {groupItems[0].attributes?.publicData.contractEndDate ? (
                          <FormattedDate
                            value={groupItems[0].attributes?.publicData.contractEndDate}
                            year="numeric"
                            month="short"
                            day="2-digit"
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {getContractDurationInMonths(
                          groupItems[0].attributes?.publicData.contractStartDate,
                          groupItems[0].attributes?.publicData.contractEndDate
                        )}
                      </td>
                      <td>
                        {totalResidualValue > 0
                          ? formatMoney(intl, subUnitsToMoney(totalResidualValue, currency))
                          : '-'}
                      </td>
                    </tr>

                    {/* Subtable for group items */}
                    {expandedGroup === contractId && (
                      <tr>
                        <td colSpan={6}>
                          <table className={css.subTable}>
                            <thead>
                              <tr>
                                <th>
                                  <FormattedMessage id="TableView.model" />
                                </th>
                                <th>
                                  <FormattedMessage id="TableView.status" />
                                </th>
                                <th>
                                  <FormattedMessage id="TableView.residualValue" />
                                </th>
                                <th>
                                  <FormattedMessage id="TableView.dealer" />
                                </th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupItems.map(listing => {
                                const isExpanded = expandedRow === listing.id.uuid;
                                return (
                                  <React.Fragment key={listing.id.uuid}>
                                    <tr
                                      className={css.clickableRow}
                                      onClick={() => toggleRow(listing.id.uuid)}
                                    >
                                      <td>{listing.attributes?.publicData?.dimrModel || '-'}</td>
                                      <td>
                                        {getStatusLabel(listing.attributes?.metadata?.status)}
                                      </td>
                                      <td>
                                        {listing.attributes?.publicData?.residualValue
                                          ? formatMoney(
                                              intl,
                                              subUnitsToMoney(
                                                listing.attributes.publicData.residualValue,
                                                listing.attributes.publicData.locale === 'no'
                                                  ? 'NOK'
                                                  : 'SEK'
                                              )
                                            )
                                          : '-'}
                                      </td>
                                      <td>
                                        {listing.attributes?.metadata
                                          ?.contractEndOwnerCompanyName || '-'}
                                      </td>
                                      <td onClick={e => e.stopPropagation()}>
                                        {renderMenu(listing)}
                                      </td>
                                    </tr>

                                    <tr className={css.expandedRow}>
                                      <td colSpan={5}>
                                        <div
                                          className={`${css.expandWrapper} ${
                                            isExpanded ? css.open : ''
                                          }`}
                                        >
                                          {isExpanded && (
                                            <ContractLeaseData
                                              listing={listing}
                                              isListView={false}
                                              className={css.leaseData}
                                              isTableView={true}
                                            />
                                          )}
                                          {isExpanded && (
                                            <div className={css.leaseActions}>
                                              <ContractLeaseActions
                                                listing={listing}
                                                isTableView={true}
                                              />
                                              <NamedLink
                                                name="ListingPage"
                                                params={{
                                                  id: listing.id.uuid,
                                                  slug: createSlug(listing.attributes.title || ''),
                                                }}
                                                to={{ state: { from: fromState } }}
                                                className={css.secondaryButtonLink}
                                              >
                                                <FormattedMessage id="TableView.viewDetails" />
                                              </NamedLink>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            : listings.map(listing => {
                const isExpanded = expandedRow === listing.id.uuid;
                return (
                  <React.Fragment key={listing.id.uuid}>
                    <tr className={css.clickableRow} onClick={() => toggleRow(listing.id.uuid)}>
                      {activeColumns.map(col => (
                        <td key={col.id}>{renderCell(listing, col)}</td>
                      ))}
                      <td onClick={e => e.stopPropagation()}>{renderMenu(listing)}</td>
                    </tr>

                    <tr className={css.expandedRow}>
                      <td colSpan={activeColumns.length + 1}>
                        <div className={`${css.expandWrapper} ${isExpanded ? css.open : ''}`}>
                          {isExpanded && (
                            <ContractLeaseData
                              listing={listing}
                              isListView={false}
                              className={css.leaseData}
                              isTableView={true}
                            />
                          )}
                          {isExpanded && (
                            <div className={css.leaseActions}>
                              <ContractLeaseActions listing={listing} isTableView={true} />
                              <NamedLink
                                name="ListingPage"
                                params={{
                                  id: listing.id.uuid,
                                  slug: createSlug(listing.attributes.title || ''),
                                }}
                                to={{ state: { from: fromState } }}
                                className={css.secondaryButtonLink}
                              >
                                <FormattedMessage id="TableView.viewDetails" />
                              </NamedLink>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
        </tbody>
      </table>
    </div>
  );
};

export default TableViewPanel;
